import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { hasAcceptedTrackingConsent } from '~/components/GDPRConsentModal';

type Props = {
  wlId?: string;
};

const DEFAULT_WL_ID = '17957';
const PROVIDER_BUY_BUTTON_SELECTOR = 'a[href*="tpembd.com/wl/redirect"]';
const PROVIDER_PROPOSAL_CARD_SELECTOR = '[class*="ProposaCard-module__root"]';
const PROVIDER_NAME_SELECTOR = '[class*="ProposaCard-module__name"]';
const PROVIDER_PRICE_SELECTOR = '[class*="ProposaCard-module__price"]';

const isTripPayoutsModalPath = (path: EventTarget[]) =>
  path.some(
    (node) =>
      node instanceof HTMLElement &&
      (node.id === 'tpwl-modals' || node.id === 'tpwl-modals-root')
  );

const getProviderName = (buyButton: HTMLAnchorElement, proposalCard: Element | null) => {
  try {
    const providerName = new URL(buyButton.href).searchParams.get('gate_name');

    if (providerName) return providerName;
  } catch {
    // Fall back to rendered provider text if the third-party URL is ever malformed.
  }

  return proposalCard?.querySelector(PROVIDER_NAME_SELECTOR)?.textContent?.trim() || 'unknown';
};

function TripPayoutsProviderBuyTracker({ wlId = DEFAULT_WL_ID }: Props) {
  useEffect(() => {
    const handleProviderBuyClick = (event: MouseEvent) => {
      const path = event.composedPath ? event.composedPath() : [];
      const buyButton = path.find(
        (node) => node instanceof HTMLAnchorElement && node.matches(PROVIDER_BUY_BUTTON_SELECTOR)
      ) as HTMLAnchorElement | undefined;

      if (!buyButton || !isTripPayoutsModalPath(path)) return;

      const proposalCard = buyButton.closest(PROVIDER_PROPOSAL_CARD_SELECTOR);
      const providerName = getProviderName(buyButton, proposalCard);
      const price = proposalCard?.querySelector(PROVIDER_PRICE_SELECTOR)?.textContent?.trim();

      if (hasAcceptedTrackingConsent()) {
        ReactGA.event({
          category: 'TripPayoutsWidget_Modal',
          action: 'Trip Payouts Widget Provider Buy Button Clicked',
          label: `wlId:${wlId};provider:${providerName}${price ? `;price:${price}` : ''}`,
        });
      }
    };

    document.addEventListener('click', handleProviderBuyClick, true);

    return () => {
      document.removeEventListener('click', handleProviderBuyClick, true);
    };
  }, [wlId]);

  return null;
}

export default TripPayoutsProviderBuyTracker;